import { supabase } from "@/integrations/supabase/client";

/**
 * Create a new offer page
 */
export const createOfferPage = async (data: {
  heading: string;
  subheading?: string;
  slug: string;
  call_to_action?: string;
  theme?: string;
}) => {
  try {
    const user = await supabase.auth.getUser();
    const userId = user.data.user?.id;

    if (!userId) {
      throw new Error("User not authenticated");
    }

    const { data: offerPage, error } = await supabase
      .from("offer_pages")
      .insert({
        heading: data.heading,
        subheading: data.subheading,
        slug: data.slug,
        theme: data.theme,
        user_id: userId,
        status: "active", // Default to active status
        is_test: import.meta.env.VITE_APP_ENV != "production",
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating offer page:", error);
      throw error;
    }

    return offerPage;
  } catch (error) {
    console.error("Error in createOfferPage:", error);
    throw error;
  }
};

/**
 * Archive or activate an offer page using the status field
 */
export const archiveOfferPage = async (
  id: string,
  currentStatus?: string
): Promise<boolean> => {
  try {
    // Toggle status between 'active' and 'archived'
    const newStatus = currentStatus === "active" ? "archived" : "active";

    const { error } = await supabase
      .from("offer_pages")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      console.error(`Error updating offer page ${id} status:`, error);
      return false;
    }

    return true;
  } catch (error) {
    console.error(`Error in archiveOfferPage for ${id}:`, error);
    return false;
  }
};

/**
 * Get all offer pages for the current user
 */
export const getOfferPages = async () => {
  try {
    // Get the current authenticated user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData.user) {
      console.error(
        "Error fetching user or user not authenticated:",
        userError
      );
      return [];
    }

    const userId = userData.user.id;

    // Filter by the current user's ID to only show their offer pages
    const { data, error } = await supabase
      .from("offer_pages")
      .select(
        `
        *,
        products:offer_page_products!offer_page_products_offer_page_id_fkey(
          product_id,
          sort_order
        )
      `
      )
      .eq("user_id", userId) // Only fetch offer pages owned by the current user
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching offer pages:", error);
      return [];
    }

    // Process the data to ensure consistent field naming and structure
    const processedData =
      data?.map((page) => ({
        ...page,
        // Map subheading to description for consistent naming
        description: page.subheading || "",
        // Use the actual status field from the database
        status: page.status || "active",
        // Keep is_test as a separate testing flag
        isTestData: page.is_test || false,
      })) || [];

    return processedData;
  } catch (error) {
    console.error("Error in getOfferPages:", error);
    return [];
  }
};

/**
 * Add products to an offer page
 */
export const addProductsToOfferPage = async (
  offerPageId: string,
  productIds: string[]
) => {
  try {
    // Create array of offer page product mappings
    const offerPageProducts = productIds.map((productId, index) => ({
      offer_page_id: offerPageId,
      product_id: productId,
      sort_order: index,
    }));

    const { data, error } = await supabase
      .from("offer_page_products")
      .insert(offerPageProducts)
      .select();

    if (error) {
      console.error("Error adding products to offer page:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error in addProductsToOfferPage:", error);
    throw error;
  }
};

/**
 * Get all products for an offer page
 */
export const getOfferPageProducts = async (offerPageId: string) => {
  try {
    // Get the current authenticated user
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData.user) {
      console.error(
        "Error fetching user or user not authenticated:",
        userError
      );
      return [];
    }

    const userId = userData.user.id;

    // First check if the offer page belongs to the current user
    const { data: offerPage, error: offerPageError } = await supabase
      .from("offer_pages")
      .select("id, user_id")
      .eq("id", offerPageId)
      .single();

    if (offerPageError || !offerPage) {
      console.error("Error fetching offer page:", offerPageError);
      return [];
    }

    // If the offer page doesn't belong to the current user, return empty array
    if (offerPage.user_id !== userId) {
      console.warn("Unauthorized access attempt to offer page:", offerPageId);
      return [];
    }

    const { data, error } = await supabase
      .from("offer_page_products")
      .select(
        `
        product_id,
        sort_order,
        products (*)
      `
      )
      .eq("offer_page_id", offerPageId)
      .order("sort_order", { ascending: true });

    if (error) {
      console.error("Error fetching offer page products:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Error in getOfferPageProducts:", error);
    return [];
  }
};

/**
 * Get an offer page by slug
 */
export const getOfferPageBySlug = async (slug: string) => {
  try {
    const { data, error } = await supabase
      .from("offer_pages")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();

    if (error) {
      console.error("Error fetching offer page by slug:", error);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error in getOfferPageBySlug:", error);
    return null;
  }
};

export const getOfferPageById = async (id: string) => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      console.error(
        "Error fetching user or user not authenticated:",
        userError
      );
      return null;
    }
    const userId = userData.user.id;
    const { data, error } = await supabase
      .from("offer_pages")
      .select(`
        *,
        products:offer_page_products!offer_page_products_offer_page_id_fkey(
          product_id,
          sort_order
        )
      `)
      .eq("id", id)
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching offer page by id:", error);
      return null;
    }

    // Process the data to ensure consistent field naming
    if (data) {
      return {
        ...data,
        // Map subheading to description for consistent naming
        description: data.subheading || "",
      };
    }

    return data;
  } catch (error) {
    console.error("Error in getOfferPageById:", error);
    return null;
  }
};

export const updateOfferPageProducts = async (
  offerPageId: string,
  productIds: string[]
) => {
  try {
    if (!productIds || productIds.length === 0) {
      throw new Error("No product IDs provided to update offer page products");
    }

    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      console.error(
        "Error fetching user or user not authenticated:",
        userError
      );
      throw new Error("User not authenticated");
    }

    const userId = userData.user.id;

    const { data: offerPage, error: offerPageError } = await supabase
      .from("offer_pages")
      .select("id, user_id")
      .eq("id", offerPageId)
      .single();

    if (offerPageError || !offerPage) {
      console.error("Error fetching offer page:", offerPageError);
      throw new Error("Offer page not found");
    }

    if (offerPage.user_id !== userId) {
      console.warn("Unauthorized access attempt to offer page:", offerPageId);
      throw new Error("Unauthorized access");
    }

    const { error: deleteError } = await supabase
      .from("offer_page_products")
      .delete()
      .eq("offer_page_id", offerPageId);

    if (deleteError) {
      console.error("Error deleting existing products:", deleteError);
      throw deleteError;
    }

    const offerPageProducts = productIds.map((productId, index) => ({
      offer_page_id: offerPageId,
      product_id: productId,
      sort_order: index,
    }));

    const { data, error: insertError } = await supabase
      .from("offer_page_products")
      .insert(offerPageProducts)
      .select();

    if (insertError) {
      console.error("Error inserting new products:", insertError);
      throw insertError;
    }
    return data;
  } catch (error) {
    console.error("Error in updateOfferPageProducts:", error);
    throw error;
  }
};

export const updateOfferPageDetails = async (
  offerPageId: string,
  data: {
    heading?: string;
    description?: string;
  }
) => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      console.error(
        "Error fetching user or user not authenticated:",
        userError
      );
      throw new Error("User not authenticated");
    }

    const userId = userData.user.id;

    const updateData: { heading?: string; subheading?: string } = {};
    if (data.heading !== undefined) updateData.heading = data.heading;
    if (data.description !== undefined) updateData.subheading = data.description; // Note: using subheading field

    const { data: result, error } = await supabase
      .from("offer_pages")
      .update(updateData)
      .eq("id", offerPageId)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating bundle page details:", error);
      throw error;
    }

    return result;
  } catch (error) {
    console.error("Error in updateOfferPageDetails:", error);
    throw error;
  }
};

export const deleteOfferPage = async (
  offerPageId: string,
  productsIds: string[]
) => {
  try {
    const currentProducts = await getOfferPageProducts(offerPageId);

    if (currentProducts.length <= productsIds.length) {
      throw new Error(
        "Cannot remove all products from an offer page. At least one product must remain."
      );
    }

    const { error } = await supabase
      .from("offer_page_products")
      .delete()
      .in("product_id", productsIds)
      .eq("offer_page_id", offerPageId);

    if (error) {
      console.error("Error deleting products from offer page:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in deleteOfferPage:", error);
    return false;
  }
};